import { Editor } from '@tiptap/react';
import { memo } from 'react';

import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';

import {
  EDITOR_TOOLBAR_ACTIONS,
  type EditorToolbarItem,
} from './editorActions';

interface ToolbarButtonProps {
  editor: Editor;
  item: EditorToolbarItem;
  active: boolean;
  disabled: boolean;
}

/**
 * A single formatting button shared by the static toolbar (`EditorToolbar`)
 * and the inline bubble menu (`EditorBubbleMenu`) so both surfaces render
 * identical controls.
 */
const ToolbarButton = memo(function ToolbarButton({
  editor,
  item,
  active,
  disabled,
}: ToolbarButtonProps) {
  const action = EDITOR_TOOLBAR_ACTIONS[item];
  // Only toggle actions (those with `isActive`) should expose `aria-pressed`;
  // one-shot actions like undo/redo/unlink are not toggle buttons.
  const isToggle = action.isActive !== undefined;
  return (
    <button
      type="button"
      aria-label={action.label}
      aria-pressed={isToggle ? active : undefined}
      className={clstx(
        'inline-flex h-8 w-8 cursor-pointer items-center justify-center gap-x-2 rounded-full border border-transparent text-sm font-semibold text-gray-800 hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-50',
        active ? 'bg-gray-200' : 'bg-transparent'
      )}
      onClick={() => action.onClick(editor)}
      disabled={disabled}
    >
      <UntitledIcon className="h-4 w-4 shrink-0" name={action.icon} />
    </button>
  );
});

export default ToolbarButton;
